import React, { useState } from 'react';
import { X, ChevronDown, ArrowLeft, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';

const countries = ['United Kingdom', 'United States', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'India', 'Singapore', 'United Arab Emirates', 'Australia', 'Canada', 'Brazil', 'Other'];

const PortalModal = ({ open, onClose, initialTab = 'signin', initialReferral = '' }) => {
  const [tab, setTab] = useState(initialTab);
  const { toast } = useToast();
  const { signIn, register } = useAuth();

  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [regData, setRegData] = useState({ firstName: '', surname: '', email: '', phone: '', country: 'United Kingdom', password: '', referral: initialReferral });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Keep the tab + referral in sync when parent passes new values (e.g., after ?ref= URL detection)
  React.useEffect(() => { setTab(initialTab); }, [initialTab]);
  React.useEffect(() => {
    if (initialReferral) setRegData((r) => ({ ...r, referral: initialReferral }));
  }, [initialReferral]);

  if (!open) return null;

  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      await signIn({ email: signInData.email, password: signInData.password });
      toast({ title: 'Welcome back', description: 'Signed in to Portal Center.' });
      onClose();
    } catch (err) {
      toast({ title: 'Sign in failed', description: err.response?.data?.detail || 'Please check credentials.' });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...regData };
      if (payload.country === 'Other') {
        payload.country = (payload.otherCountry || '').trim() || 'Other';
      }
      delete payload.otherCountry;
      await register(payload);
      toast({
        title: 'Account created — Verify your email',
        description: `We've sent a verification link to ${payload.email}. You can start exploring now — verify anytime from the banner.`,
      });
      onClose();
    } catch (err) {
      toast({ title: 'Registration failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await API.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast({ title: 'Request failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setForgotBusy(false);
    }
  };

  const goBackToSignIn = () => {
    setTab('signin');
    setForgotSent(false);
    setForgotEmail('');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-[560px] max-h-[92vh] overflow-y-auto rounded-3xl bg-[#fbf4dc] shadow-2xl border border-emerald-900/20">
        <button onClick={onClose} className="absolute top-4 right-4 h-9 w-9 rounded-full hover:bg-emerald-900/10 flex items-center justify-center text-emerald-950 z-10" data-testid="portal-close-btn">
          <X size={18} />
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-serif text-[30px] text-emerald-950 tracking-tight">
              {tab === 'forgot' ? 'Reset Password' : 'Portal Center'}
            </h2>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
            </span>
          </div>

          {tab !== 'forgot' && (
            <div className="mt-7 grid grid-cols-2 gap-2 rounded-full bg-emerald-100/70 p-1.5">
              <button
                onClick={() => setTab('signin')}
                className={`rounded-full py-2.5 text-sm font-medium transition ${tab === 'signin' ? 'bg-emerald-950 text-emerald-50' : 'text-emerald-900/70 hover:text-emerald-900'}`}
              >Sign In</button>
              <button
                onClick={() => setTab('register')}
                className={`rounded-full py-2.5 text-sm font-medium transition ${tab === 'register' ? 'bg-emerald-950 text-emerald-50' : 'text-emerald-900/70 hover:text-emerald-900'}`}
              >Register</button>
            </div>
          )}

          {tab === 'signin' && (
            <form onSubmit={handleSignIn} className="mt-7 space-y-5">
              <Field label="Email Address">
                <input type="email" required placeholder="name@example.com" className="portal-input" value={signInData.email} onChange={(e) => setSignInData({ ...signInData, email: e.target.value })} />
              </Field>
              <Field label="Password">
                <input type="password" required placeholder="Enter account password" className="portal-input" value={signInData.password} onChange={(e) => setSignInData({ ...signInData, password: e.target.value })} />
              </Field>
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  onClick={() => { setForgotEmail(signInData.email); setTab('forgot'); }}
                  className="text-sm text-emerald-800 hover:text-emerald-950 font-medium"
                  data-testid="forgot-password-link"
                >Forgot Password?</button>
              </div>
              <button type="submit" className="w-full rounded-full bg-emerald-950 hover:bg-emerald-900 text-emerald-50 font-medium py-3.5 transition">
                Sign In Account
              </button>
            </form>
          )}

          {tab === 'register' && (
            <form onSubmit={handleRegister} className="mt-7 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="First Name"><input required placeholder="Jane" className="portal-input" value={regData.firstName} onChange={(e) => setRegData({ ...regData, firstName: e.target.value })} /></Field>
                <Field label="Surname"><input required placeholder="Doe" className="portal-input" value={regData.surname} onChange={(e) => setRegData({ ...regData, surname: e.target.value })} /></Field>
              </div>
              <Field label="Email Address"><input type="email" required placeholder="jane.doe@example.com" className="portal-input" value={regData.email} onChange={(e) => setRegData({ ...regData, email: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Phone Number"><input required placeholder="+44 7700 900077" className="portal-input" value={regData.phone} onChange={(e) => setRegData({ ...regData, phone: e.target.value })} /></Field>
                <Field label="Country">
                  <div className="relative">
                    <select className="portal-input appearance-none pr-8" value={regData.country} onChange={(e) => setRegData({ ...regData, country: e.target.value })}>
                      {countries.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-900/60 pointer-events-none" />
                  </div>
                  {regData.country === 'Other' && (
                    <input
                      required
                      placeholder="Type your country"
                      className="portal-input mt-2"
                      value={regData.otherCountry || ''}
                      onChange={(e) => setRegData({ ...regData, otherCountry: e.target.value })}
                      data-testid="register-other-country-input"
                    />
                  )}
                </Field>
              </div>
              <Field label="Create Security Password"><input type="password" required minLength={8} placeholder="Minimum 8 characters" className="portal-input" value={regData.password} onChange={(e) => setRegData({ ...regData, password: e.target.value })} /></Field>
              <Field label="Sponsor / Referral Code (Optional)"><input placeholder="E.G. SGW-XXXXX" className="portal-input" value={regData.referral} onChange={(e) => setRegData({ ...regData, referral: e.target.value })} /></Field>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900/85">
                <b>Note:</b> After registration, we'll email you a verification link. You can log in right away, but verify within 48 hours to keep your account active.
              </div>
              <button type="submit" className="w-full rounded-full bg-emerald-950 hover:bg-emerald-900 text-emerald-50 font-medium py-3.5 transition">
                Create Live Account
              </button>
            </form>
          )}

          {tab === 'forgot' && (
            <div className="mt-7">
              <button type="button" onClick={goBackToSignIn} className="inline-flex items-center gap-1.5 text-sm text-emerald-800 hover:text-emerald-950 mb-4" data-testid="forgot-back-btn">
                <ArrowLeft size={14} /> Back to Sign In
              </button>
              {!forgotSent ? (
                <form onSubmit={handleForgot} className="space-y-5">
                  <p className="text-sm text-emerald-900/70">Enter the email associated with your account. We'll send you a secure link to reset your password.</p>
                  <Field label="Email Address">
                    <input
                      type="email" required autoFocus
                      placeholder="name@example.com"
                      className="portal-input"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={forgotBusy}
                    className="w-full rounded-full bg-emerald-950 hover:bg-emerald-900 disabled:opacity-60 text-emerald-50 font-medium py-3.5 transition"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotBusy ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
              ) : (
                <div className="text-center py-6" data-testid="forgot-success">
                  <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 border border-emerald-500/40 flex items-center justify-center mb-4">
                    <Mail size={26} className="text-emerald-700" />
                  </div>
                  <div className="font-serif text-emerald-950 text-xl mb-2">Check your inbox</div>
                  <p className="text-sm text-emerald-900/70 leading-relaxed">
                    If an account exists for <b>{forgotEmail}</b>, you'll receive a reset link within a minute. The link expires in 1 hour.
                  </p>
                  <button
                    onClick={goBackToSignIn}
                    className="mt-6 rounded-full bg-emerald-950 hover:bg-emerald-900 text-emerald-50 text-sm font-medium px-6 py-2.5 transition"
                  >
                    Back to Sign In
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/80 mb-2">{label}</span>
    {children}
  </label>
);

export default PortalModal;
